import type {
  Api,
  BaseType,
  IntegrationType,
  PlanFeatureTypes,
  TeamDetailV3V3Type,
  TeamMemberV3ResponseV3Type,
  TeamMembersAddV3ReqV3Type,
  TeamMembersRemoveV3ReqV3Type,
  TeamMembersUpdateV3ReqV3Type,
  TeamTreeNodeV3Type,
  TeamUpdateV3ReqV3Type,
  TeamV3V3Type,
  WorkspaceType,
  WorkspaceUserType,
} from 'nocodb-sdk'
import { PlanLimitTypes, TeamUserRoles, WorkspaceStatus, WorkspaceUserRoles } from 'nocodb-sdk'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { isString } from '@vue/shared'
import { userLocalStorageInfoManager } from '#imports'

export interface NcWorkspace extends WorkspaceType {
  edit?: boolean
  temp_title?: string | null
  roles?: string
  payment?: {
    plan: {
      title: string
      limit: { [key in PlanLimitTypes]: number }
      features: { [key in PlanFeatureTypes]: boolean }
      meta: { [key in PlanLimitTypes]: number } & {
        [key in PlanFeatureTypes]: boolean
      }
    }
  }
  stats?: { [key in PlanLimitTypes]: number }
  grace_period_start_at?: string | null
  stripe_customer_id?: string
  segment_code?: number
}

export const useWorkspace = defineStore('workspaceStore', () => {
  // todo: update type in swagger
  const basesStore = useBases()

  const deletingWorkspace = ref(false)

  const ssoLoginRequiredDlg = ref(false)

  const mfaSetupRequiredDlg = ref(false)

  const { loadRoles, isUIAllowed } = useRoles()

  const { user: currentUser } = useGlobal()

  const { isFeatureEnabled } = useBetaFeatureToggle()

  const collaborators = ref<WorkspaceUserType[] | null>()

  const collaboratorsMap = computed(() => {
    return (collaborators.value || [])?.reduce((acc, curr) => {
      if (curr.email) {
        acc[curr.email] = curr
      }

      if (curr.fk_user_id) {
        acc[curr.fk_user_id] = curr
      }

      return acc
    }, {} as Record<string, WorkspaceUserType>)
  })

  const allCollaborators = ref<WorkspaceUserType[] | null>()

  const lastPopulatedWorkspaceId = ref<string | null>(null)

  const router = useRouter()

  const route = router.currentRoute

  const { $api } = useNuxtApp()

  const { refreshCommandPalette } = useCommandPalette()

  const { $e } = useNuxtApp()

  const { appInfo, ncNavigateTo, lastOpenedWorkspaceId, storage } = useGlobal()

  const nocoAi = useNocoAi()

  const workspaceUserCount = ref<number | undefined>(undefined)

  const workspaceOwnerCount = ref<number | undefined>(undefined)

  const isSharedBase = computed(() => route.value.params.typeOrId === 'base')

  const isWorkspaceSettingsPageOpened = computed(() => route.value.name === 'index-typeOrId-settings-page')

  const isIntegrationsPageOpened = computed(
    () =>
      route.value.name === 'index-typeOrId-integrations' ||
      (route.value.name === 'index-typeOrId-settings-page' && route.value.params.page === 'ws-integrations'),
  )

  const isTemplatesPageOpened = computed(() => (route.value.name as string)?.startsWith('index-typeOrId-templates'))

  const isTemplatesFeatureEnabled = computed(
    () =>
      isFeatureEnabled(FEATURE_FLAG.TEMPLATES) && (appInfo.value.isCloud || (isEeUI && process.env.NODE_ENV === 'development')),
  )

  const isFeedPageOpened = computed(() => route.value.name === 'index-typeOrId-feed')

  const workspaces = ref<Map<string, NcWorkspace>>(new Map())
  const workspacesList = computed<NcWorkspace[]>(() =>
    Array.from(workspaces.value.values()).sort((a, b) => a.updated_at - b.updated_at),
  )

  const isWorkspaceCeLocked = (workspaceId?: string) => {
    if (!workspaceId) return false
    if (!appInfo.value.isOnPrem || appInfo.value.ee) return false
    if (!appInfo.value.defaultWorkspaceId) return false
    return workspaceId !== appInfo.value.defaultWorkspaceId
  }

  const isWorkspaceLoading = ref(true)
  const isWorkspacesLoading = ref(true)
  const isCollaboratorsLoading = ref(true)
  const isInvitingCollaborators = ref(false)

  const upgradeWsDlg = ref(false)
  const upgradeWsJobId = ref<string | null>(null)

  const removingCollaboratorMap = ref<Record<string, boolean>>({})

  const activePage = computed<'workspace' | 'recent' | 'shared' | 'starred'>(
    () => (route.value.query.page as 'workspace' | 'recent' | 'shared' | 'starred') ?? 'workspace',
  )

  const activeWorkspaceId = computed(() => {
    if (route.value.query.workspaceId) return route.value.query.workspaceId as string

    if (route.value.params.typeOrId) return route.value.params.typeOrId as string

    if (route.value.params.workspaceId) return route.value.params.workspaceId as string

    // On unlicensed on-prem, prefer the default workspace to avoid 402 errors
    // when the last-opened workspace is locked (non-default).
    const defaultWsId = appInfo.value.defaultWorkspaceId
    if (defaultWsId && isWorkspaceCeLocked(lastOpenedWorkspaceId.value)) {
      const defaultWs = workspacesList.value.find((w) => w.id === defaultWsId)
      if (defaultWs) return defaultWs.id
    }

    const lastLoadedWorkspace = workspacesList.value.find((w) => w.id === lastOpenedWorkspaceId.value)
    if (lastLoadedWorkspace) return lastLoadedWorkspace.id

    return workspacesList.value?.[0]?.id
  })

  const activeWorkspace = computed(() => {
    if (activeWorkspaceId.value && workspaces.value?.has(activeWorkspaceId.value)) {
      const ws = workspaces.value.get(activeWorkspaceId.value)
      if (ws) {
        return ws
      }
    }
    return activePage.value === 'workspace' ? workspacesList.value?.[0] ?? null : null
  })

  const activeWorkspaceMeta = computed<Record<string, any>>(() => {
    const defaultMeta = {}
    if (!activeWorkspace.value) return defaultMeta
    try {
      return (
        (isString(activeWorkspace.value.meta) ? JSON.parse(activeWorkspace.value.meta) : activeWorkspace.value.meta) ??
        defaultMeta
      )
    } catch (e) {
      return defaultMeta
    }
  })

  /** actions */
  const loadWorkspaces = async (ignoreError = false) => {
    try {
      // todo: pagination
      const { list, pageInfo: _ } = await $api.workspace.list()

      const newWorkspaceIds = new Set(list?.map((w) => w.id!) ?? [])

      // Update or insert current workspaces
      for (const workspace of list ?? []) {
        const oldData = workspaces.value.has(workspace.id!) ? workspaces.value.get(workspace.id!) : {}
        workspaces.value.set(workspace.id!, {
          ...oldData,
          ...workspace,
        })
      }

      userLocalStorageInfoManager.cleanMissingWorkspaces(currentUser.value?.id, Array.from(newWorkspaceIds))

      // Remove stale workspaces
      for (const existingId of workspaces.value.keys()) {
        if (!newWorkspaceIds.has(existingId)) {
          workspaces.value.delete(existingId)
        }
      }

      isWorkspacesLoading.value = false

      return list
    } catch (e: any) {
      if (!ignoreError) message.error(await e)
    }
  }

  const createWorkspace = async (workspace: Pick<WorkspaceType, 'title' | 'order' | 'description' | 'meta' | 'fk_org_id'>) => {
    try {
      let reqPayload

      if (workspace.title!.includes(',')) {
        reqPayload = workspace
          .title!.split(',')
          .filter((t) => t.trim())
          .map((title) => {
            // pick a random color from array and assign to workspace
            const color = baseThemeColors[Math.floor(Math.random() * 1000) % baseThemeColors.length]
            return { ...workspace, title, meta: { color, icon: '', iconType: '' } }
          })
      } else {
        // pick a random color from array and assign to workspace
        const color = baseThemeColors[Math.floor(Math.random() * 1000) % baseThemeColors.length]
        reqPayload = { ...workspace, meta: { color, icon: '', iconType: '' } }
      }

      // todo: pagination
      const workspaceRes = await $api.workspace.create(reqPayload)
      refreshCommandPalette()
      return workspaceRes
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const updateWorkspace = async (
    workspaceId: string,
    workspaceData: Pick<WorkspaceType, 'title' | 'order' | 'description' | 'meta'>,
  ) => {
    try {
      // todo: pagination
      await $api.workspace.update(workspaceId, workspaceData, {
        baseURL: appInfo.value.baseHostName ? `https://${workspaceId}.${appInfo.value.baseHostName}` : undefined,
      })

      const workspace = workspaces.value.get(workspaceId)
      if (!workspace) return

      workspaces.value.set(workspaceId, {
        ...workspace,
        ...workspaceData,
      })
      refreshCommandPalette()
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const deleteWorkspace = async (workspaceId: string, { _skipStateUpdate }: { skipStateUpdate?: boolean } = {}) => {
    // todo: pagination
    await $api.workspace.delete(workspaceId, {
      baseURL: appInfo.value.baseHostName ? `https://${workspaceId}.${appInfo.value.baseHostName}` : undefined,
    })

    await loadWorkspaces()

    refreshCommandPalette()
  }

  const loadCollaborators = async (
    params?: { offset?: number; limit?: number; ignoreLoading?: boolean; includeDeleted?: boolean },
    workspaceId?: string,
  ) => {
    if (!workspaceId && !activeWorkspace.value?.id) {
      throw new Error('Workspace not selected')
    }

    if (!params?.ignoreLoading) isCollaboratorsLoading.value = true

    try {
      // todo: pagination
      const { list, pageInfo } = await $api.workspaceUser.list(
        workspaceId ?? activeWorkspace.value.id!,
        { ...params },
        {
          baseURL: appInfo.value.baseHostName
            ? `https://${workspaceId ?? activeWorkspace.value.id!}.${appInfo.value.baseHostName}`
            : undefined,
        },
      )

      allCollaborators.value = list
      collaborators.value = (list || [])?.filter((u: any) => !u?.deleted)
      workspaceUserCount.value = pageInfo.totalRows
      workspaceOwnerCount.value = collaborators.value?.filter((u: any) => u.roles === WorkspaceUserRoles.OWNER).length
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    } finally {
      if (!params?.ignoreLoading) isCollaboratorsLoading.value = false
    }
  }

  // invite new user to the workspace
  const inviteCollaborator = async (email: string, roles: WorkspaceUserRoles, workspaceId?: string) => {
    if (!workspaceId && !activeWorkspace.value?.id) {
      throw new Error('Workspace not selected')
    }

    isInvitingCollaborators.value = true
    try {
      await $api.workspaceUser.invite(
        workspaceId ?? activeWorkspace.value.id!,
        {
          email,
          roles,
        },
        {
          baseURL: appInfo.value.baseHostName
            ? `https://${workspaceId ?? activeWorkspace.value.id!}.${appInfo.value.baseHostName}`
            : undefined,
        },
      )
      $e('a:workspace:settings:invite-user')
      await loadCollaborators({} as any, workspaceId)
      basesStore.clearBasesUser()
    } finally {
      isInvitingCollaborators.value = false
    }
  }

  // remove user from workspace
  const removeCollaborator = async (userId: string, workspaceId?: string, onCurrentUserLeftCallback?: () => void) => {
    if (removingCollaboratorMap.value[userId]) return

    try {
      if (!workspaceId && !activeWorkspace.value?.id) {
        throw new Error('Workspace not selected')
      }

      removingCollaboratorMap.value[userId] = true

      await $api.workspaceUser.delete(workspaceId ?? activeWorkspace.value.id!, userId, {
        baseURL: appInfo.value.baseHostName
          ? `https://${workspaceId ?? activeWorkspace.value.id!}.${appInfo.value.baseHostName}`
          : undefined,
      })

      $e('a:workspace:settings:remove-user')

      // if user left the workspace, navigate to home
      if (currentUser.value?.id === userId) {
        onCurrentUserLeftCallback?.()
        userLocalStorageInfoManager.clearWorkspace(currentUser.value?.id, workspaceId ?? activeWorkspace.value.id!)

        const list = await loadWorkspaces()
        message.success(`You’ve left the workspace. Switched to ${list?.[0]?.title ?? 'another'} workspace.`)
        return await navigateTo(`/${list?.[0]?.id}`)
      }

      await loadCollaborators({} as any, workspaceId)

      basesStore.clearBasesUser()
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    } finally {
      delete removingCollaboratorMap.value[userId]
    }
  }

  // update existing collaborator role
  const updateCollaborator = async (
    userId: string,
    roles: WorkspaceUserRoles,
    workspaceId?: string,
    _overrideBaseRole: boolean = false,
  ) => {
    try {
      if (!workspaceId && !activeWorkspace.value?.id) {
        throw new Error('Workspace not selected')
      }

      await $api.workspaceUser.update(
        workspaceId,
        userId,
        {
          roles,
          /**
           * Todo: @rameshmane7218 enable this `overrideBaseRole` after adding support in backend
           */
          // overrideBaseRole,
        },
        {
          baseURL: appInfo.value.baseHostName ? `https://${activeWorkspace.value.id!}.${appInfo.value.baseHostName}` : undefined,
        },
      )

      // reload roles if updating roles of current user
      if (userId === currentUser.value?.id) {
        loadRoles(undefined, {}, workspaceId).catch(() => {
          // ignore
        })
      }

      await loadCollaborators({} as any, workspaceId)
      basesStore.clearBasesUser()
      return true
    } catch (e) {
      const errorInfo = await extractSdkResponseErrorMsgv2(e)

      if (appInfo.value?.isCloud && !appInfo.value?.isOnPrem && errorInfo.error === NcErrorType.ERR_PLAN_LIMIT_EXCEEDED) {
        throw e
      } else {
        message.error(errorInfo.message)
      }
    }
  }

  const loadWorkspace = async (workspaceId: string) => {
    try {
      const res = await $api.workspace.read(workspaceId, {
        baseURL: appInfo.value.baseHostName ? `https://${workspaceId}.${appInfo.value.baseHostName}` : undefined,
      })

      workspaces.value.set(res.workspace.id!, res.workspace)

      nocoAi.aiIntegrations.value =
        (res.workspace as WorkspaceType & { integrations: Partial<IntegrationType>[] })?.integrations || []

      workspaceUserCount.value = Number(res.workspaceUserCount)
      workspaceOwnerCount.value = Number(res.workspaceOwnerCount)

      if (res.workspace.db_job_id) {
        upgradeWsJobId.value = res.workspace.db_job_id
        upgradeWsDlg.value = true
      }

      return res
    } catch (e: any) {
      // Skip the toast + home redirect when the error is the force_2fa
      // setup gate — the API interceptor already opened the setup-required
      // dialog and is owning the redirect to /account/security?openEnrollment=true
      // once the user clicks OK. Falling through here would race the
      // dialog's navigateTo with this navigateTo('/'), and the home page
      // would usually win → broken UX (and broken playwright assertion).
      const errorInfo = await extractSdkResponseErrorMsgv2(e)
      if (errorInfo.error === NcErrorType.ERR_MFA_SETUP_REQUIRED) {
        return
      }
      message.error(errorInfo.message)
      navigateTo('/')
    }
  }

  async function populateWorkspace({ force, workspaceId: _workspaceId }: { force?: boolean; workspaceId?: string } = {}) {
    isWorkspaceLoading.value = true
    const workspaceId = _workspaceId ?? activeWorkspaceId.value!

    if (!isWorkspacesLoading.value && workspaceId && isWorkspaceCeLocked(workspaceId)) {
      isWorkspaceLoading.value = false
      return
    }

    lastPopulatedWorkspaceId.value = workspaceId

    const wsState = workspaces.value.get(workspaceId)

    if (force || !wsState || !(wsState as any)?.payment) {
      await loadWorkspace(workspaceId)
      await loadRoles(route.value.params.baseId as string, {}, workspaceId)
    }

    if (activeWorkspace.value?.status === WorkspaceStatus.CREATED) {
      await basesStore.loadProjects()
    }
    isWorkspaceLoading.value = false
  }

  const addToFavourite = async (baseId: string) => {
    try {
      const bases = basesStore.bases
      const base = bases.get(baseId)
      if (!base) return

      // todo: update the type
      base.starred = true

      await $api.base.userMetaUpdate(
        baseId,
        {
          starred: true,
        },
        {
          baseURL: appInfo.value.baseHostName ? `https://${activeWorkspace.value?.id}.${appInfo.value.baseHostName}` : undefined,
        },
      )
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const removeFromFavourite = async (baseId: string) => {
    try {
      const base = basesStore.bases.get(baseId)
      if (!base) return

      base.starred = false

      await $api.base.userMetaUpdate(
        baseId,
        {
          starred: false,
        },
        {
          baseURL: appInfo.value.baseHostName ? `https://${activeWorkspace.value?.id}.${appInfo.value.baseHostName}` : undefined,
        },
      )
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const updateProjectTitle = async (base: BaseType & { edit: boolean; temp_title: string }, workspaceId?: string) => {
    try {
      await $api.base.update(
        base.id!,
        { title: base.temp_title },
        {
          baseURL: appInfo.value.baseHostName
            ? `https://${workspaceId || activeWorkspace.value?.id || base.fk_workspace_id}.${appInfo.value.baseHostName}`
            : undefined,
        },
      )
      base.title = base.temp_title
      base.edit = false
      refreshCommandPalette()
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const moveWorkspace = async (workspaceId: string, baseId: string) => {
    try {
      await $api.workspaceBase.move(workspaceId, baseId, {
        baseURL: appInfo.value.baseHostName ? `https://${activeWorkspace.value?.id}.${appInfo.value.baseHostName}` : undefined,
      })
      message.success('Base moved successfully')
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const moveToOrg = async (workspaceId: string, orgId: string) => {
    try {
      await ($api as Api<any>).orgWorkspace.add(orgId, workspaceId)
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  async function saveTheme(_theme: Partial<ThemeConfig>, _workspaceId?: string) {
    /* const fullTheme = {
      primaryColor: theme.value.primaryColor,
      accentColor: theme.value.accentColor,
      ..._theme,
    }

    await updateWorkspace(workspaceId || activeWorkspace.value!.id!, {
      meta: {
        ...(workspaceId ? workspaces.value.get(workspaceId) : activeWorkspace.value),
        theme: fullTheme,
      },
    })

    setTheme(fullTheme)

    $e('c:themes:change') */
  }

  const clearWorkspaces = async () => {
    await basesStore.clearBases()
    workspaces.value.clear()
  }
  const upgradeActiveWorkspace = async () => {
    const workspace = activeWorkspace.value
    if (!workspace) {
      throw new Error('Workspace not selected')
    }
    await $api.workspace.upgrade(workspace.id!, {
      baseURL: appInfo.value.baseHostName ? `https://${activeWorkspace.value?.id}.${appInfo.value.baseHostName}` : undefined,
    })
    await loadWorkspaces()
  }

  const navigateToWorkspace = async (workspaceId?: string) => {
    workspaceId = workspaceId || activeWorkspaceId.value!
    if (!workspaceId) {
      throw new Error('Workspace not selected')
    }
    ncNavigateTo({ workspaceId })
  }

  const navigateToWorkspaceSettings = async (workspaceId?: string, cmdOrCtrl?: boolean) => {
    workspaceId = workspaceId || activeWorkspaceId.value!
    if (!workspaceId) {
      throw new Error('Workspace not selected')
    }

    const path = `/${workspaceId}/more`
    if (cmdOrCtrl) {
      await navigateTo(path, { open: navigateToBlankTargetOpenOption })
    } else {
      await navigateTo(path)
    }
  }

  const navigateToFeed = async (workspaceId?: string, cmdOrCtrl?: boolean, query: Record<string, string> = {}) => {
    workspaceId = workspaceId || activeWorkspaceId.value!

    if (cmdOrCtrl) {
      await navigateTo(router.resolve({ name: 'index-typeOrId-feed', params: { typeOrId: workspaceId }, query }).href, {
        open: navigateToBlankTargetOpenOption,
      })
    } else {
      router.push({ name: 'index-typeOrId-feed', params: { typeOrId: workspaceId }, query })
    }
  }

  const navigateToTemplates = async (workspaceId?: string, cmdOrCtrl?: boolean, query: Record<string, string> = {}) => {
    workspaceId = workspaceId || activeWorkspaceId.value!

    if (cmdOrCtrl) {
      await navigateTo(router.resolve({ name: 'index-typeOrId-templates', params: { typeOrId: workspaceId }, query }).href, {
        open: navigateToBlankTargetOpenOption,
      })
    } else {
      router.push({ name: 'index-typeOrId-templates', params: { typeOrId: workspaceId }, query })
    }
  }

  const navigateToIntegrations = async (workspaceId?: string, cmdOrCtrl?: boolean, query: Record<string, string> = {}) => {
    workspaceId = workspaceId || activeWorkspaceId.value!

    if (!workspaceId) {
      throw new Error('Workspace not selected')
    }

    if (cmdOrCtrl) {
      await navigateTo(
        router.resolve({
          name: 'index-typeOrId-integrations',
          params: { typeOrId: workspaceId },
          query,
        }).href,
        {
          open: navigateToBlankTargetOpenOption,
        },
      )
    } else {
      router.push({ name: 'index-typeOrId-integrations', params: { typeOrId: workspaceId }, query })
    }
  }

  function setLoadingState(isLoading = false) {
    isWorkspaceLoading.value = isLoading
  }

  const workspaceRole = computed(() => activeWorkspace.value?.roles)

  const getPlanLimit = (limitType: PlanLimitTypes) => {
    const limit = activeWorkspace.value?.payment?.plan?.meta?.[limitType] ?? Infinity
    return limit === -1 ? Infinity : limit
  }

  const toggleSsoLoginRequiredDlg = (show = !ssoLoginRequiredDlg.value) => {
    ssoLoginRequiredDlg.value = show
  }

  const toggleMfaSetupRequiredDlg = (show = !mfaSetupRequiredDlg.value) => {
    mfaSetupRequiredDlg.value = show
  }

  /**
   * Teams section start here
   */

  const isTeamsEnabled = computed(() => isEeUI)

  const isTeamsLoading = ref(true)

  const teams = ref<TeamV3V3Type[]>([])

  const teamsMap = computed(() => {
    return (teams.value || [])?.reduce((acc, curr) => {
      if (curr.id) {
        acc[curr.id] = curr
      }
      return acc
    }, {} as Record<string, TeamV3V3Type>)
  })

  const editTeamDetails = ref<TeamDetailV3V3Type | null>(null)

  async function loadTeams({ workspaceId }: { workspaceId: string }) {
    const { blockTeamsManagement } = useEeConfig()

    // Workspace belongs to an org — org teams are always available regardless of plan
    const hasOrg = !!activeWorkspace.value?.fk_org_id

    if (!isTeamsEnabled.value || isSharedBaseOrErdOrViewRoute(route.value)) {
      teams.value = []
      isTeamsLoading.value = false
      return
    }

    // If team management is blocked and workspace has no org, skip loading
    if (blockTeamsManagement.value && !hasOrg && !activeWorkspace.value?.payment?.plan?.meta) {
      teams.value = []
      isTeamsLoading.value = false
      return
    }

    isTeamsLoading.value = true

    try {
      const { list } = await $api.internal.getOperation(workspaceId, NO_SCOPE, {
        operation: 'teamList',
      })

      teams.value = list
    } catch (e: any) {
      teams.value = []
      // Silently ignore forbidden errors (e.g. free plan without team management access)
      if (e?.response?.status !== 403) {
        message.error(await extractSdkResponseErrorMsg(e))
      }
    } finally {
      isTeamsLoading.value = false
    }
  }

  async function getTeamById(workspaceId: string, teamId: string) {
    if (!isTeamsEnabled.value) return

    if (!isUIAllowed('teamGet')) return

    try {
      const teamDetails = (await $api.internal.getOperation(workspaceId, NO_SCOPE, {
        operation: 'teamGet',
        teamId,
      })) as TeamDetailV3V3Type

      editTeamDetails.value = teamDetails

      return teamDetails
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  async function createTeam(
    workspaceId: string,
    team: Pick<TeamType, 'title' | 'description' | 'meta'> & { parent_team_id?: string },
  ) {
    if (!isTeamsEnabled.value) return

    try {
      const res = (await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'teamCreate',
        },
        team,
      )) as TeamV3V3Type

      $e('a:team:create')

      if (!res) return

      teams.value.push(res)

      if (activeWorkspace.value) {
        const newStatsCount = Math.max(0, (activeWorkspace.value?.stats?.[PlanLimitTypes.LIMIT_TEAM_MANAGEMENT] ?? 0) + 1)

        workspaces.value.set(activeWorkspace.value.id!, {
          ...activeWorkspace.value,
          stats: {
            ...(activeWorkspace.value?.stats || {}),
            [PlanLimitTypes.LIMIT_TEAM_MANAGEMENT]: newStatsCount,
          },
        })
      }

      return res
    } catch (error: any) {
      console.error(error)

      const errorInfo = await extractSdkResponseErrorMsgv2(error)

      if (errorInfo?.error === NcErrorType.ERR_PLAN_LIMIT_EXCEEDED) {
        message.error(errorInfo.message)
      } else {
        message.error('Error occured while creating new team')
      }
    }
  }

  async function deleteTeam(workspaceId: string, teamId: string, force?: boolean) {
    if (!isTeamsEnabled.value) return

    try {
      const res = (await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'teamDelete',
        },
        {
          teamId,
          ...(force ? { force: true } : {}),
        },
      )) as TeamV3V3Type

      $e('a:team:delete')

      if (!res) return

      // If force deleted, reparent children locally to the deleted team's parent
      if (force) {
        const deletedTeam = teams.value.find((t) => t.id === teamId)
        const parentId = (deletedTeam as any)?.fk_parent_team_id || null

        teams.value = teams.value.map((t) => {
          if ((t as any).fk_parent_team_id === teamId) {
            return { ...t, fk_parent_team_id: parentId }
          }
          return t
        })
      }

      teams.value = teams.value.filter((t) => t.id !== teamId)

      if (activeWorkspace.value) {
        const newStatsCount = Math.max(0, (activeWorkspace.value?.stats?.[PlanLimitTypes.LIMIT_TEAM_MANAGEMENT] ?? 0) - 1)

        workspaces.value.set(activeWorkspace.value.id!, {
          ...activeWorkspace.value,
          stats: {
            ...(activeWorkspace.value?.stats || {}),
            [PlanLimitTypes.LIMIT_TEAM_MANAGEMENT]: newStatsCount,
          },
        })
      }

      return res
    } catch (error: any) {
      console.error(error)
      message.error('Error occured while deleting team')
    }
  }

  async function updateTeam(workspaceId: string, teamId: string, updates: Partial<TeamUpdateV3ReqV3Type>) {
    if (!isTeamsEnabled.value) return

    if (!updates) return

    try {
      const res = (await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'teamUpdate',
        },
        {
          teamId,
          ...updates,
        },
      )) as TeamV3V3Type

      $e('a:team:update')

      if (!res) return

      teams.value = teams.value.map((team) => (team.id === teamId ? { ...team, ...res } : team))

      return res
    } catch (error: any) {
      console.error(error)
      message.error('Error occured while updating team')
    }
  }

  // ── Team Hierarchy ─────────────────────────────────────────────

  const teamTree = ref<TeamTreeNodeV3Type[]>([])

  async function loadTeamTree(workspaceId: string) {
    const { blockTeamsManagement } = useEeConfig()

    if (!activeWorkspace.value?.payment?.plan?.meta || !isTeamsEnabled.value || blockTeamsManagement.value) {
      teamTree.value = []
      return
    }

    try {
      const { list } = await $api.internal.getOperation(workspaceId, NO_SCOPE, {
        operation: 'teamTree',
      })

      teamTree.value = list as TeamTreeNodeV3Type[]
    } catch (e: any) {
      teamTree.value = []
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  async function moveTeam(workspaceId: string, teamId: string, parentTeamId: string | null) {
    if (!isTeamsEnabled.value) return

    try {
      const res = await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'teamMove',
        },
        {
          teamId,
          parent_team_id: parentTeamId,
        },
      )

      $e('a:team:move')

      // Reload both flat list and tree
      await Promise.all([loadTeams({ workspaceId }), loadTeamTree(workspaceId)])

      return res
    } catch (error: any) {
      message.error(await extractSdkResponseErrorMsg(error))
    }
  }

  function getTeamBreadcrumb(teamId: string): TeamV3V3Type[] {
    const result: TeamV3V3Type[] = []
    let currentId: string | null | undefined = teamId

    while (currentId) {
      const team = teamsMap.value[currentId]
      if (!team) break
      result.unshift(team)
      currentId = (team as any).fk_parent_team_id
    }

    return result
  }

  function getTeamDescendantIds(teamId: string): string[] {
    const result: string[] = []
    const team = teamsMap.value[teamId]
    if (!team?.path) return result

    for (const t of teams.value) {
      if ((t as any).path?.startsWith(`${team.path}/`)) {
        result.push(t.id)
      }
    }

    return result
  }

  const onAddTeamMembers = (teamId: string, addedMembers: TeamMemberV3ResponseV3Type[]) => {
    if (editTeamDetails.value && ncIsArray(addedMembers)) {
      editTeamDetails.value.members = [...(editTeamDetails.value.members || []), ...addedMembers]
    }

    if (teamsMap.value[teamId]) {
      const addedOwners = addedMembers
        .filter(
          (member) =>
            member.team_role === TeamUserRoles.OWNER && !(teamsMap.value[teamId]?.managers || []).includes(member.user_id),
        )
        .map((m) => m.user_id)

      teams.value = teams.value.map((team) =>
        team.id === teamId
          ? {
              ...team,
              members_count: (team.members_count || 0) + addedMembers.length,
              managers_count: (team.managers_count || 0) + addedOwners.length,
              managers: [...(team.managers || []), ...addedOwners],
              is_member: addedMembers.some((am) => am.user_id === currentUser.value?.id) ? true : team.is_member,
            }
          : team,
      )
    }
  }

  async function addTeamMembers(
    workspaceId: string = activeWorkspaceId.value!,
    teamId: string,
    members: TeamMembersAddV3ReqV3Type[],
  ) {
    if (!isTeamsEnabled.value) return

    try {
      const addedMembers = (await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'teamMembersAdd',
        },
        {
          teamId,
          members,
        },
      )) as TeamMemberV3ResponseV3Type[]

      $e('a:team:members-add', {
        teamId,
      })

      if (!addedMembers) return []

      onAddTeamMembers(teamId, addedMembers)

      return addedMembers
    } catch (e: any) {
      console.error('Failed to add members', e)
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const onRemoveTeamMembers = (teamId: string, removedMembers: TeamMembersRemoveV3ReqV3Type[]) => {
    if (editTeamDetails.value && ncIsArray(removedMembers)) {
      editTeamDetails.value.members = (editTeamDetails.value.members || []).filter(
        (member) => !removedMembers.find((rm) => rm.user_id === member.user_id),
      )
    }

    if (teamsMap.value[teamId]) {
      const teamOwners = (teamsMap.value[teamId]?.managers || []).filter((m) => !removedMembers.some((rm) => rm.user_id === m))

      teams.value = teams.value.map((team) =>
        team.id === teamId
          ? {
              ...team,
              members_count: team.members_count - removedMembers.length,
              managers_count: teamOwners.length,
              managers: teamOwners,
              is_member:
                team.is_member && removedMembers.some((rm) => rm.user_id === currentUser.value?.id) ? false : team.is_member,
            }
          : team,
      )
    }
  }

  async function removeTeamMembers(
    workspaceId: string = activeWorkspaceId.value!,
    teamId: string,
    members: TeamMembersRemoveV3ReqV3Type[],
  ) {
    if (!isTeamsEnabled.value) return

    try {
      const removedMembers = (await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'teamMembersRemove',
        },
        {
          teamId,
          members,
        },
      )) as TeamMembersRemoveV3ReqV3Type[]

      $e('a:team:members-remove', {
        teamId,
      })

      if (!removedMembers) return []

      onRemoveTeamMembers(teamId, removedMembers)

      return removedMembers
    } catch (e: any) {
      console.error('Failed to remove members', e)
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const onUpdateTeamMembers = (teamId: string, updatedMembers: TeamMemberV3ResponseV3Type[]) => {
    if (editTeamDetails.value && ncIsArray(updatedMembers)) {
      editTeamDetails.value.members = (editTeamDetails.value.members || []).map((member) => {
        const updatedMember = updatedMembers.find((um) => um.user_id === member.user_id)
        if (!updatedMember) return member

        return {
          ...member,
          ...updatedMember,
        }
      })
    }

    if (teamsMap.value[teamId]) {
      let teamOwners = [] as string[]

      if (editTeamDetails.value) {
        teamOwners = editTeamDetails.value.members
          .filter((member) => member.team_role === TeamUserRoles.OWNER)
          .map((m) => m.user_id)
      } else {
        teamOwners = teamsMap.value[teamId]?.managers || []

        updatedMembers.forEach((member) => {
          if (member.team_role === TeamUserRoles.OWNER && !teamOwners.includes(member.user_id)) {
            teamOwners.push(member.user_id)
          } else if (member.team_role !== TeamUserRoles.OWNER && teamOwners.includes(member.user_id)) {
            teamOwners = teamOwners.filter((owner) => owner !== member.user_id)
          }
        })
      }

      teams.value = teams.value.map((team) =>
        team.id === teamId
          ? {
              ...team,
              managers_count: teamOwners.length,
              managers: teamOwners,
            }
          : team,
      )
    }
  }

  async function updateTeamMembers(
    workspaceId: string = activeWorkspaceId.value!,
    teamId: string,
    members: TeamMembersUpdateV3ReqV3Type[],
  ) {
    if (!isTeamsEnabled.value) return

    try {
      const updatedMembers = (await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'teamMembersUpdate',
        },
        {
          teamId,
          members,
        },
      )) as TeamMemberV3ResponseV3Type[]

      $e('a:team:members-update', {
        teamId,
      })

      onUpdateTeamMembers(teamId, updatedMembers || [])

      return updatedMembers || []
    } catch (e: any) {
      console.error('Failed to update members', e)
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  const isLoadingWorkspaceTeams = ref(true)

  const workspaceTeams = ref<Record<string, any>>([])

  async function workspaceTeamList(workspaceId: string = activeWorkspaceId.value!, showLoading = true) {
    const { blockTeamsManagement } = useEeConfig()

    const hasOrg = !!activeWorkspace.value?.fk_org_id

    if (!isTeamsEnabled.value || !workspaceId || isSharedBaseOrErdOrViewRoute(route.value)) {
      workspaceTeams.value = []
      isLoadingWorkspaceTeams.value = false
      return
    }

    if (blockTeamsManagement.value && !hasOrg && !activeWorkspace.value?.payment?.plan?.meta) {
      workspaceTeams.value = []
      isLoadingWorkspaceTeams.value = false
      return
    }

    if (showLoading) {
      isLoadingWorkspaceTeams.value = true
    }

    try {
      const { list } = await $api.internal.getOperation(workspaceId, NO_SCOPE, {
        operation: 'workspaceTeamList',
      })

      workspaceTeams.value = list || []

      return list || []
    } catch (e: any) {
      workspaceTeams.value = []
      // Silently ignore forbidden errors (e.g. free plan without team management access)
      if (e?.response?.status !== 403) {
        message.error(await extractSdkResponseErrorMsg(e))
      }
    } finally {
      isLoadingWorkspaceTeams.value = false
    }
  }

  async function workspaceTeamGet(workspaceId: string, teamId: string) {
    const { blockTeamsManagement } = useEeConfig()

    if (!isTeamsEnabled.value || blockTeamsManagement.value) return

    try {
      const teamDetails = (await $api.internal.getOperation(workspaceId, NO_SCOPE, {
        operation: 'workspaceTeamGet',
        teamId,
      })) as TeamDetailV3V3Type

      return teamDetails
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  async function workspaceTeamAdd(
    workspaceId: string = activeWorkspaceId.value!,
    teams: {
      team_id: string
      workspace_role: Exclude<WorkspaceUserRoles, WorkspaceUserRoles.OWNER>
    }[],
  ) {
    const { blockTeamsManagement } = useEeConfig()

    const hasOrg = !!activeWorkspace.value?.fk_org_id

    if (!isTeamsEnabled.value || (blockTeamsManagement.value && !hasOrg)) return

    try {
      const res = await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'workspaceTeamAdd',
        },
        teams,
      )

      $e('a:workspace:team-add')

      if (!res) return

      workspaceTeams.value.push(...(ncIsArray(res) ? res : [res]))
      return ncIsArray(res) ? res : [res]
    } finally {
      // catch error is handled in inviteDlg
    }
  }

  async function workspaceTeamUpdate(
    workspaceId: string,
    updates: {
      team_id: string
      workspace_role: Exclude<WorkspaceUserRoles, WorkspaceUserRoles.OWNER>
    },
  ) {
    const { blockTeamsManagement } = useEeConfig()

    if (!isTeamsEnabled.value || blockTeamsManagement.value || !updates.team_id) return

    try {
      const res = await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'workspaceTeamUpdate',
        },
        updates,
      )

      $e('a:workspace:team-update')

      if (!res) return

      workspaceTeams.value = workspaceTeams.value.map((team) => (team.team_id === updates.team_id ? { ...team, ...res } : team))

      return res
    } catch (e: any) {
      const errorInfo = await extractSdkResponseErrorMsgv2(e)

      if (appInfo.value?.isCloud && !appInfo.value?.isOnPrem && errorInfo.error === NcErrorType.ERR_PLAN_LIMIT_EXCEEDED) {
        throw e
      } else {
        message.error(errorInfo.message)
      }
    }
  }

  async function workspaceTeamRemove(workspaceId: string = activeWorkspaceId.value!, teamIds: string[]) {
    const { blockTeamsManagement } = useEeConfig()

    if (!isTeamsEnabled.value || blockTeamsManagement.value || !teamIds.length) return

    try {
      const res = await $api.internal.postOperation(
        workspaceId,
        NO_SCOPE,
        {
          operation: 'workspaceTeamRemove',
        },
        teamIds.map((teamId) => ({ team_id: teamId })),
      )

      $e('a:workspace:team-remove')

      workspaceTeams.value = workspaceTeams.value.filter((team) => !teamIds.includes(team.team_id))

      return ncIsArray(res) ? res : [res]
    } catch (e: any) {
      message.error(await extractSdkResponseErrorMsg(e))
    }
  }

  /**
   * Teams section end here
   */

  /**
   * Watchers
   */
  watch(activeWorkspaceId, async () => {
    // Redirect away from locked workspaces (unlicensed on-prem, non-default)
    if (activeWorkspaceId.value && isWorkspaceCeLocked(activeWorkspaceId.value)) {
      const defaultWsId = appInfo.value.defaultWorkspaceId
      // Only redirect if the default workspace is accessible (in the loaded list)
      if (defaultWsId && workspaces.value.has(defaultWsId)) {
        navigateTo(`/${defaultWsId}`, { replace: true })
        return
      }
      // No accessible non-locked workspace — stay put, don't load roles
      return
    }

    await loadRoles(undefined, {}, activeWorkspaceId.value)
  })

  // When plan data or workspace changes, trigger team loading if teams are available
  watch(
    () => [activeWorkspace.value?.payment?.plan?.meta, activeWorkspace.value?.id, activeWorkspace.value?.fk_org_id],
    () => {
      if (!activeWorkspace.value?.id) return

      // Skip team loading for CE-locked workspaces
      if (isWorkspaceCeLocked(activeWorkspace.value.id)) return

      const planMeta = activeWorkspace.value?.payment?.plan?.meta
      const hasOrg = !!activeWorkspace.value?.fk_org_id

      // Skip if no plan data AND no org (nothing to load)
      if (!planMeta && !hasOrg) return

      const { blockTeamsManagement } = useEeConfig()

      // Skip if team management is blocked AND workspace has no org
      if (blockTeamsManagement.value && !hasOrg) return

      loadTeams({ workspaceId: activeWorkspace.value?.id as string }).catch(() => {
        // ignore
      })
      workspaceTeamList(activeWorkspace.value?.id as string).catch(() => {
        // ignore
      })
    },
    { immediate: true },
  )

  watch(
    () => activeWorkspace.value?.id,
    () => {
      if (!activeWorkspaceId.value) return
      if (isWorkspaceCeLocked(activeWorkspaceId.value)) return
      storage.value.lastOpenedWorkspaceId = activeWorkspaceId.value
    },
  )

  return {
    loadWorkspaces,
    workspaces,
    workspacesList,
    isWorkspaceCeLocked,
    createWorkspace,
    deleteWorkspace,
    updateWorkspace,
    activeWorkspace,
    loadCollaborators,
    inviteCollaborator,
    removeCollaborator,
    updateCollaborator,
    collaborators,
    collaboratorsMap,
    allCollaborators,
    isInvitingCollaborators,
    isCollaboratorsLoading,
    addToFavourite,
    removeFromFavourite,
    activeWorkspaceId,
    activePage,
    updateProjectTitle,
    moveWorkspace,
    loadWorkspace,
    saveTheme,
    activeWorkspaceMeta,
    isWorkspaceLoading,
    isWorkspacesLoading,
    populateWorkspace,
    clearWorkspaces,
    upgradeActiveWorkspace,
    navigateToWorkspace,
    setLoadingState,
    isSharedBase,
    navigateToWorkspaceSettings,
    lastPopulatedWorkspaceId,
    workspaceRole,
    isWorkspaceSettingsPageOpened,
    workspaceUserCount,
    workspaceOwnerCount,
    getPlanLimit,
    moveToOrg,
    isIntegrationsPageOpened,
    navigateToIntegrations,
    navigateToFeed,
    navigateToTemplates,
    isTemplatesPageOpened,
    isTemplatesFeatureEnabled,
    isFeedPageOpened,
    deletingWorkspace,
    ssoLoginRequiredDlg,
    toggleSsoLoginRequiredDlg,
    mfaSetupRequiredDlg,
    toggleMfaSetupRequiredDlg,
    upgradeWsDlg,
    upgradeWsJobId,
    removingCollaboratorMap,

    // Teams
    teams,
    teamsMap,
    isTeamsEnabled,
    isTeamsLoading,
    editTeamDetails,
    createTeam,
    deleteTeam,
    updateTeam,
    loadTeams,
    getTeamById,
    teamTree,
    loadTeamTree,
    moveTeam,
    getTeamBreadcrumb,
    getTeamDescendantIds,
    addTeamMembers,
    onAddTeamMembers,
    removeTeamMembers,
    onRemoveTeamMembers,
    updateTeamMembers,
    onUpdateTeamMembers,

    // Workspace Teams
    isLoadingWorkspaceTeams,
    workspaceTeams,
    workspaceTeamList,
    workspaceTeamGet,
    workspaceTeamAdd,
    workspaceTeamUpdate,
    workspaceTeamRemove,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWorkspace as any, import.meta.hot))
}
